import React from 'react';
import styled, { keyframes } from 'styled-components';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import {
  Container,
  CustomerHeaderContainer,
  DashboardLayout,
  MainContent,
  Sidebar,
  MetricsGrid,
  TransactionCard,
  TransactionHeader,
  TabsContainer,
  QuickStatsCard,
  PatternsCard,
} from './StyledComponents';

// Shimmer animation
const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// Base skeleton element with theme-aware gradient
const SkeletonBase = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? `linear-gradient(
          90deg,
          rgba(255, 255, 255, 0.03) 0%,
          rgba(255, 255, 255, 0.08) 50%,
          rgba(255, 255, 255, 0.03) 100%
        )`
      : `linear-gradient(
          90deg,
          rgba(0, 0, 0, 0.04) 0%,
          rgba(0, 0, 0, 0.08) 50%,
          rgba(0, 0, 0, 0.04) 100%
        )`};
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: ${(props) => props.radius || '4px'};
`;

// Customer Header Skeleton Components
const SkeletonAvatar = styled(SkeletonBase)`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
  }
`;

const SkeletonCustomerName = styled(SkeletonBase)`
  height: 28px;
  width: 200px;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    height: 24px;
    width: 150px;
  }
`;

const SkeletonCustomerMeta = styled(SkeletonBase)`
  height: 16px;
  width: ${(props) => props.width || '150px'};
  margin-bottom: 4px;
`;

const SkeletonLifetimeValue = styled(SkeletonBase)`
  height: 36px;
  width: 140px;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    height: 32px;
    width: 120px;
  }
`;

const SkeletonLifetimeLabel = styled(SkeletonBase)`
  height: 14px;
  width: 100px;
`;

// Metrics Skeleton
const SkeletonMetricCard = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255,255,255,0.05)'
      : 'rgba(255,255,255,0.95)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgba(0,0,0,0.08)'};
  border-radius: 8px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const SkeletonMetricValue = styled(SkeletonBase)`
  height: 32px;
  width: 120px;
`;

const SkeletonMetricLabel = styled(SkeletonBase)`
  height: 16px;
  width: 100px;
`;

const SkeletonMetricChange = styled(SkeletonBase)`
  height: 14px;
  width: 80px;
`;

// Transaction Skeleton Components
const SkeletonTab = styled(SkeletonBase)`
  height: 36px;
  width: 80px;
  border-radius: 20px;
  display: inline-block;
`;

const SkeletonTransactionItem = styled.div`
  padding: 20px;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.05)'
        : 'rgba(0,0,0,0.05)'};
  display: flex;
  gap: 16px;
  align-items: center;
`;

const SkeletonDateBadge = styled(SkeletonBase)`
  width: 60px;
  height: 60px;
  border-radius: 8px;
  flex-shrink: 0;
`;

const SkeletonEventInfo = styled.div`
  flex: 1;
`;

const SkeletonEventName = styled(SkeletonBase)`
  height: 20px;
  width: 250px;
  margin-bottom: 8px;
`;

const SkeletonEventSummary = styled(SkeletonBase)`
  height: 16px;
  width: 180px;
`;

const SkeletonAmount = styled(SkeletonBase)`
  height: 24px;
  width: 80px;
`;

// Sidebar Skeleton Components
const SkeletonQuickActionButton = styled(SkeletonBase)`
  height: 40px;
  width: 100%;
  border-radius: 8px;
  margin-bottom: 8px;
`;

const SkeletonQuickStatItem = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px 0;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.05)'
        : 'rgba(0,0,0,0.05)'};
`;

const SkeletonStatIcon = styled(SkeletonBase)`
  width: 32px;
  height: 32px;
  border-radius: 50%;
`;

const SkeletonStatContent = styled.div`
  flex: 1;
`;

const SkeletonStatLabel = styled(SkeletonBase)`
  height: 12px;
  width: 80px;
  margin-bottom: 6px;
`;

const SkeletonStatValue = styled(SkeletonBase)`
  height: 20px;
  width: 120px;
`;

// Open Credits Skeleton components removed - not needed since
// OpenCreditsCard returns null when no credits exist

// Pattern Skeleton
const SkeletonPatternSection = styled.div`
  margin-bottom: 20px;
`;

const SkeletonPatternTitle = styled(SkeletonBase)`
  height: 16px;
  width: 120px;
  margin-bottom: 12px;
`;

const SkeletonPatternValue = styled(SkeletonBase)`
  height: 24px;
  width: 160px;
`;

const CustomerDetailSkeleton = () => {
  return (
    <EnhancedThemeProvider>
      <Container>
        {/* Customer Header Skeleton */}
        <CustomerHeaderContainer>
          <div style={{ display: 'flex', gap: '16px', flex: 1 }}>
            <SkeletonAvatar />
            <div style={{ flex: 1 }}>
              <SkeletonCustomerName />
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <SkeletonCustomerMeta width="120px" />
                <SkeletonCustomerMeta width="150px" />
                <SkeletonCustomerMeta width="100px" />
              </div>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
            }}
          >
            <SkeletonLifetimeValue />
            <SkeletonLifetimeLabel />
          </div>
        </CustomerHeaderContainer>

        {/* Metrics Grid Skeleton */}
        <MetricsGrid>
          {[1, 2, 3, 4].map((index) => (
            <SkeletonMetricCard key={index}>
              <SkeletonMetricValue />
              <SkeletonMetricLabel />
              <SkeletonMetricChange />
            </SkeletonMetricCard>
          ))}
        </MetricsGrid>

        {/* Dashboard Layout */}
        <DashboardLayout>
          <MainContent>
            {/* Transaction History Skeleton */}
            <TransactionCard>
              <TransactionHeader>
                <SkeletonBase style={{ height: '24px', width: '150px' }} />
              </TransactionHeader>

              {/* Tabs Skeleton */}
              <TabsContainer>
                {[1, 2, 3, 4].map((index) => (
                  <SkeletonTab key={index} style={{ marginRight: '8px' }} />
                ))}
              </TabsContainer>

              {/* Transaction Items Skeleton */}
              <div>
                {[1, 2, 3, 4, 5, 6].map((index) => (
                  <SkeletonTransactionItem key={index}>
                    <SkeletonDateBadge />
                    <SkeletonEventInfo>
                      <SkeletonEventName />
                      <SkeletonEventSummary />
                    </SkeletonEventInfo>
                    <SkeletonAmount />
                  </SkeletonTransactionItem>
                ))}
              </div>

              {/* Pagination Skeleton */}
              <div
                style={{
                  padding: '20px',
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                {[1, 2, 3, 4, 5].map((index) => (
                  <SkeletonBase
                    key={index}
                    style={{
                      height: '32px',
                      width: '32px',
                      borderRadius: '6px',
                    }}
                  />
                ))}
              </div>
            </TransactionCard>
          </MainContent>

          <Sidebar>
            {/* Quick Actions Skeleton */}
            <div style={{ marginBottom: '16px' }}>
              <SkeletonQuickActionButton />
              <SkeletonQuickActionButton />
            </div>

            {/* Quick Stats Skeleton */}
            <QuickStatsCard>
              <SkeletonBase
                style={{ height: '20px', width: '100px', marginBottom: '16px' }}
              />
              <SkeletonQuickStatItem>
                <SkeletonStatIcon />
                <SkeletonStatContent>
                  <SkeletonStatLabel />
                  <SkeletonStatValue />
                </SkeletonStatContent>
              </SkeletonQuickStatItem>
              <SkeletonQuickStatItem>
                <SkeletonStatIcon />
                <SkeletonStatContent>
                  <SkeletonStatLabel />
                  <SkeletonStatValue />
                </SkeletonStatContent>
              </SkeletonQuickStatItem>
              <SkeletonQuickStatItem style={{ borderBottom: 'none' }}>
                <SkeletonStatIcon />
                <SkeletonStatContent>
                  <SkeletonStatLabel />
                  <SkeletonStatValue />
                </SkeletonStatContent>
              </SkeletonQuickStatItem>
            </QuickStatsCard>

            {/* Open Credits Skeleton - Removed to match actual component behavior */}
            {/* The actual OpenCreditsCard returns null when no credits exist */}

            {/* Patterns Skeleton */}
            <PatternsCard>
              <SkeletonBase
                style={{ height: '20px', width: '120px', marginBottom: '20px' }}
              />

              <SkeletonPatternSection>
                <SkeletonPatternTitle />
                <SkeletonPatternValue />
              </SkeletonPatternSection>

              <SkeletonPatternSection>
                <SkeletonPatternTitle />
                <div
                  style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}
                >
                  {[1, 2, 3].map((index) => (
                    <SkeletonBase
                      key={index}
                      style={{
                        height: '28px',
                        width: '60px',
                        borderRadius: '14px',
                      }}
                    />
                  ))}
                </div>
                <SkeletonPatternValue />
              </SkeletonPatternSection>

              <SkeletonPatternSection style={{ marginBottom: 0 }}>
                <SkeletonPatternTitle />
                <SkeletonPatternValue />
              </SkeletonPatternSection>
            </PatternsCard>
          </Sidebar>
        </DashboardLayout>
      </Container>
    </EnhancedThemeProvider>
  );
};

export default CustomerDetailSkeleton;
